/**
 * Sign in.
 *
 * The port of `Views/LoginPage.xaml` + `LoginViewModel`. Two differences worth naming:
 *
 *   * the password is read straight off the field. OpenSilver's `PasswordBox` had no bindable
 *     `Password`, so every page taking one grew a code-behind click handler - and on the network page
 *     a visual-tree walk to find the right box in a row. A Webwin `TextBox` with a `passwordChar` is
 *     just a control this function is holding, so all of that disappears.
 *   * the card is width-capped rather than fixed, for the reason the XAML gives: the shell disables
 *     horizontal scrolling, so a card clipped at phone width is one nobody can reach the bottom of.
 */
import { insets, type Container } from 'webwin';
import * as auth from '../api/auth.js';
import * as router from '../router.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  capWidth,
  card,
  field,
  GAP,
  h1,
  muted,
  passwordBox,
  stackOf,
  textBox,
  vstack
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

export function loginPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      // The browser Back button can land here after signing in. An authenticated user has no
      // business on the login screen - the same bounce `LoginPage.OnNavigatedTo` performs.
      if (auth.isSignedIn() && !auth.mustChangePassword()) {
        router.start();
        return;
      }

      const status = new StatusLine();
      const email = textBox();
      const password = passwordBox();
      let busy = false;

      // A password manager can only fill an <input>, never a field drawn on the canvas, so Webwin
      // lays a real, invisible one over each box inside a real <form> and mirrors values both ways.
      // Disposed with the page - after a successful sign-in, which is when a browser decides
      // whether to offer to save.
      const autofill = ctx.desktop.beginAutofill([
        { control: email, purpose: 'username' },
        { control: password, purpose: 'current-password' }
      ]);
      ctx.dispose.add(() => autofill.dispose());

      const submit = async (): Promise<void> => {
        // Re-entrancy refused here rather than by disabling the button: Enter can fire while a click
        // is still out, and a control disabled across an await that never returns stays disabled.
        if (busy) return;
        busy = true;
        status.busy('Signing in…');
        try {
          const error = await auth.signIn(email.text.trim(), password.text);
          if (ctx.cancelled()) return;
          if (error !== null) {
            status.error(error);
            return;
          }
          status.clear();
          // The page never navigates, so the browser has no submit to notice; hand it the
          // credential instead. Not awaited: the sign-in is done whatever the manager decides.
          void autofill.save();
          // A seeded account owes a password change: the API answers 403 on every other endpoint
          // until it is done, so routing anywhere else would land on a page of refusals.
          if (auth.mustChangePassword()) router.navigate('/change-password', true);
          else router.start();
        } finally {
          busy = false;
        }
      };

      const onEnter = (e: { key: string }): void => {
        if (e.key === 'Enter') void submit();
      };
      email.on('keydown', onEnter);
      password.on('keydown', onEnter);

      const form = card({
        hAlign: 'center',
        vAlign: 'center',
        padding: insets(24),
        width: 360
      });
      form.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h1('Sign in'),
          muted('Jetsonsoft CRM'),
          field('Email', email),
          field('Password', password),
          status.label,
          button('Sign in', () => void submit(), true, { hAlign: 'stretch', autoWidth: false })
        )
      );

      host.controls.add(form);
      capWidth(host, form, 360);
      email.focus();
    }
  };
}
